import React, { useMemo } from 'react';

import dayjs from '~/timeInit';

import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { TourFormDeparture } from '~/services/ToursService';

import { departureSelectorKey, groupDeparturesForSelector } from './departureRows';

interface Props {
  departures: Array<TourFormDeparture>;
  selectedDepartureKey: string;
  onSelectDeparture: (departureKey: string) => void;
}

function formatDate(date?: string): string {
  return date ? dayjs(date).format('D MMM YYYY') : '';
}

export default function TourDepartureSelector({ departures, selectedDepartureKey, onSelectDeparture }: Props) {
  const departureGroups = useMemo(
    () => groupDeparturesForSelector<TourFormDeparture>(departures).map(([, group]) => group),
    [departures],
  );

  return (
    <TextField
      select
      label="Departure"
      value={selectedDepartureKey}
      onChange={(event) => onSelectDeparture(event.target.value)}
      sx={{ minWidth: 420, maxWidth: 560 }}
      InputLabelProps={{ shrink: true }}
      SelectProps={{
        MenuProps: { PaperProps: { sx: { maxHeight: 420 } } },
        displayEmpty: true,
        renderValue: (value) => {
          const departure = departureGroups.find((group) => departureSelectorKey(group[0]) === value)?.[0];

          return departure ? `${departure.departureName} · ${formatDate(departure.departureDate)}` : 'All';
        },
      }}
    >
      <MenuItem value="">
        <Typography variant="body2">All</Typography>
      </MenuItem>

      {departureGroups.map(([departure]) => (
        <MenuItem key={departureSelectorKey(departure)} value={departureSelectorKey(departure)}>
          <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ width: '100%' }}>
            <Typography variant="body2" sx={{ minWidth: 116 }}>
              {departure.departureName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(departure.departureDate)}
            </Typography>
            <Box flex={1} />
          </Stack>
        </MenuItem>
      ))}
    </TextField>
  );
}
